import { useState } from "react";
import PageTitle from "../layout/PageTitle";
import Button from "../layout/Button";
import EditQueueModal from "./EditQueueModal";
import MenuBreadcrumb from "./menu/MenuBreadcrumb";
import MenuOptionCard from "./menu/MenuOptionCard";
import AddMenuOptionCard from "./menu/AddMenuOptionCard";
import MenuOptionModal, {
  MenuOptionModalResult,
} from "./menu/MenuOptionModal";
import MenuNodeMessageModal from "./menu/MenuNodeMessageModal";
import { IvrHook } from "../../hooks/IvrPageHook";
import {
  AudioMessage,
  IvrMenuNode,
  IvrMenuOption,
  Queue,
} from "../../model/queue.model";
import {
  buildMenuBreadcrumbs,
  emptyMenuNode,
  findMenuNode,
  nextAvailableDigit,
  removeQueueReferences,
  updateNodeAtPath,
} from "../../model/ivrMenu.utils";

function MenuConfig({ ivrHook }: MenuConfigProps) {
  const [path, setPath] = useState<number[]>([]);
  const [optionModal, setOptionModal] = useState<{
    mode: "add" | "edit";
    option?: IvrMenuOption;
  } | null>(null);
  const [messageModalShown, setMessageModalShown] = useState(false);
  const [editQueue, setEditQueue] = useState<Queue | false>(false);

  const root = ivrHook.menu || emptyMenuNode();
  const currentNode = findMenuNode(root, path);
  const crumbs = buildMenuBreadcrumbs(root, path);
  const nextDigit = nextAvailableDigit(currentNode.options);

  const mutateMenu = (updater: (node: IvrMenuNode) => IvrMenuNode) => {
    ivrHook.setMenu(updateNodeAtPath(root, path, updater));
    ivrHook.setHasUnsavedChanges(true);
  };

  const handleSaveOption = (result: MenuOptionModalResult) => {
    let newOption: IvrMenuOption;

    if (result.kind === "submenu") {
      const editingOption = optionModal?.option;
      const node =
        editingOption?.type === "submenu" ? editingOption.node : emptyMenuNode();
      newOption = {
        option: result.option,
        name: result.name,
        type: "submenu",
        node,
      };
    } else if (result.kind === "existing-queue") {
      newOption = {
        option: result.option,
        name: result.name,
        type: "queue",
        queueId: result.queueId,
      };
    } else {
      const newQueue: Queue = {
        tempId: result.newQueue.tempId,
        name: result.newQueue.name,
        option: result.newQueue.option,
        strategy: result.newQueue.strategy,
        participants: result.newQueue.participants,
      };
      ivrHook.setQueues((prev) => [...prev, newQueue]);
      newOption = {
        option: result.option,
        name: result.name,
        type: "queue",
        queueId: result.newQueue.tempId,
      };
    }

    const editingDigit = optionModal?.option?.option;

    mutateMenu((node) => ({
      ...node,
      options:
        optionModal?.mode === "edit"
          ? node.options.map((o) => (o.option === editingDigit ? newOption : o))
          : [...node.options, newOption],
    }));

    setOptionModal(null);
  };

  const handleDeleteOption = (option: IvrMenuOption) => {
    mutateMenu((node) => ({
      ...node,
      options: node.options.filter((o) => o.option !== option.option),
    }));
  };

  const handleEditQueueOption = (option: IvrMenuOption) => {
    if (option.type !== "queue") return;
    const queue = ivrHook.queues.find(
      (q) => (q.id || q.tempId) === option.queueId
    );
    if (queue) setEditQueue(queue);
  };

  const onQueueUpdateHandler = (data: Queue) => {
    ivrHook.setQueues((prev) => [
      ...prev.filter((v) => (v.id ? v.id !== data.id : v.tempId !== data.tempId)),
      data,
    ]);
    ivrHook.setHasUnsavedChanges(true);
    setEditQueue(false);
  };

  const onQueueDeleteHandler = (id: string) => {
    ivrHook.setQueues((prev) => prev.filter((v) => v.id !== id));
    ivrHook.setMenu(removeQueueReferences(root, id));
    ivrHook.setHasUnsavedChanges(true);
    setEditQueue(false);
  };

  const handleSaveMessage = (
    message: AudioMessage,
    error: AudioMessage | undefined
  ) => {
    mutateMenu((node) => ({ ...node, message, error }));
    setMessageModalShown(false);
  };

  return (
    <>
      <PageTitle tooltipMessageId="ivr-queues">
        {path.length === 0 ? "Suas Opções" : "Opções deste Nível"}
      </PageTitle>

      <MenuBreadcrumb crumbs={crumbs} onNavigate={setPath} />

      {path.length > 0 && (
        <div className="bg-white border-2 border-white-dark rounded-15 py-4 px-6 mb-4 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-gray-dark text-[11px] font-bold uppercase tracking-wide mb-1">
              Mensagem de saudação deste nível
            </div>
            <div className="text-primary font-medium text-sm">
              {currentNode.message ? "Configurada" : "Ainda não configurada"}
            </div>
          </div>
          <Button outline onClick={() => setMessageModalShown(true)}>
            Editar mensagem
          </Button>
        </div>
      )}

      <div className="grid grid-cols-10 gap-4 2xl:grid-cols-ivrCard grid-cols-ivrCard-sm">
        {currentNode.options
          .slice()
          .sort((a, b) => a.option - b.option)
          .map((option) => (
            <MenuOptionCard
              key={option.option}
              option={option}
              queue={
                option.type === "queue"
                  ? ivrHook.queues.find(
                      (q) => (q.id || q.tempId) === option.queueId
                    )
                  : undefined
              }
              onOpenSubmenu={() => setPath([...path, option.option])}
              onEditOption={() => setOptionModal({ mode: "edit", option })}
              onEditQueue={() => handleEditQueueOption(option)}
              onDelete={() => handleDeleteOption(option)}
              className="w-full h-full hover:ring-expand hover:drop-shadow-far transition-filter ring-white duration-200"
            />
          ))}
        <AddMenuOptionCard
          onCreate={() => setOptionModal({ mode: "add" })}
          disabled={nextDigit === null}
          nextDigit={nextDigit}
        />
      </div>

      <MenuOptionModal
        shown={!!optionModal}
        mode={optionModal?.mode || "add"}
        option={optionModal?.option}
        initialDigit={nextDigit ?? undefined}
        existingOptions={currentNode.options}
        queues={ivrHook.queues}
        onClose={() => setOptionModal(null)}
        onSave={handleSaveOption}
      />

      <MenuNodeMessageModal
        shown={messageModalShown}
        node={currentNode}
        path={path}
        queues={ivrHook.queues}
        onClose={() => setMessageModalShown(false)}
        onSave={handleSaveMessage}
      />

      <EditQueueModal
        shown={!!editQueue}
        queue={editQueue}
        onClose={() => setEditQueue(false)}
        onSave={onQueueUpdateHandler}
        onDelete={onQueueDeleteHandler}
        queues={ivrHook.queues}
      />
    </>
  );
}

export default MenuConfig;

export type MenuConfigProps = {
  ivrHook: IvrHook;
};
